<script setup>
import { inject, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import useCartStore from '../store/cart';
import useOrderStore from '../store/order';
import { useCustomerStore } from '../store/customer';
import { useScheduleStore } from '../store/schedule';
import LineItems from './LineItems.vue';
import OrderTotal from './OrderTotal.vue';
import ModalHead from './ModalHead.vue';
import ModalFooter from './ModalFooter.vue';
import ModalBtn from './ModalBtn.vue';
import Payment from './Payment.vue';
import router from '../routes';
import { useCloverSDK } from '@client/js/composables/useCloverSDK.js';
import { useUberTracking } from '@client/js/composables/useUberTracking.js';
import { FLOW, PAYMENT } from '../shared/constants';

const emit = defineEmits(['close']);

const rootData = inject('rootData');
const { t, te } = useI18n();

const { isSdkReady } = useCloverSDK();

const cartStore = useCartStore();
const customerStore = useCustomerStore();
const scheduleStore = useScheduleStore();
const orderStore = useOrderStore();
const address = String(customerStore.isDeliveryOrder ? `${String(customerStore?.street ?? '')}, ${String(customerStore?.city ?? '')}, ${String(customerStore?.state ?? '')}, ${String(customerStore?.zip ?? '')}` : '')

// Uber Direct courier tracking. start() is a no-op unless the site offers Uber
// and we have an order id, so pickup / merchant-fulfilled orders never poll.
const uber = useUberTracking(rootData);
const { status: uberStatus, trackingUrl: uberTrackingUrl, courierName: uberCourier, courierVehicle: uberVehicle } = uber;
// Courier status → localized label. Unknown/empty statuses fall back to the raw
// value rather than showing a missing-key string.
const uberStatusLabel = computed(() => {
    const key = `thankyou.courierStatus.${uberStatus.value}`;
    return te(key) ? t(key) : uberStatus.value;
});
onMounted(() => {
    if (customerStore.isDeliveryOrder && orderStore.orderId) {
        uber.start(String(orderStore.orderId));
    }
});

// const selectedDaySaved = localStorage.getItem(dateStorageKey);
// const selectedTimeSaved = localStorage.getItem(timeStorageKey);

const returnToMenu = () => {
    // orderStore.orderId = '';
    orderStore.paymentStatus = '';
    orderStore.paymentMessage = '';
    emit('close');
    cartStore.clear();
    router.push('/cart/');
}
</script>

<template>
    <ModalHead></ModalHead>
    <div class="modal__body">
        <div class="thanks">
            <div class="thanks__details">
                <div class="thanks__title">{{ t('thankyou.title') }}</div>
                <template v-if="orderStore.orderId">
                    {{ t('thankyou.orderNumber') }}<span class="thanks__order-id" v-text="orderStore.orderId"></span><br />
                    <div v-if="orderStore.paymentStatus && rootData?.ordering?.flow === FLOW.ONE">
                        {{ t('thankyou.payment') }} <span class="thanks__payment-code" v-if="orderStore?.paymentStatus">{{ orderStore.paymentStatus }}</span>
                        <div class="thanks__payment-details" v-if="orderStore?.paymentMessage">{{ orderStore.paymentMessage }}</div>
                        <div v-show="orderStore.paymentStatus != 'PAID'">
                            <div class="thanks__payment-link">
                                {{ t('thankyou.paymentLink') }} <router-link :to="`/order-payment/${orderStore.orderId}`">{{ t('thankyou.checkPaymentState') }}</router-link>
                            </div>
                        </div>
                    </div>
                    <br />
                    <div class="thanks__desc">
                        {{ t('thankyou.desc') }}
                    </div>
                    <div class="thanks__tracking" v-if="uberStatus">
                        <div class="thanks__tracking-title">{{ t('thankyou.trackingTitle') }}</div>
                        <div class="thanks__tracking-status">{{ t('thankyou.status') }} <strong>{{ uberStatusLabel }}</strong></div>
                        <div v-if="uberCourier">{{ t('thankyou.courier') }} {{ uberCourier }}<span v-if="uberVehicle"> ({{ uberVehicle }})</span></div>
                        <div v-if="uberTrackingUrl" class="thanks__tracking-link">
                            <a :href="uberTrackingUrl" target="_blank" rel="noopener">{{ t('thankyou.trackDelivery') }}</a>
                        </div>
                    </div>
                    <div class="thanks__note" v-show="rootData?.ordering?.flow !== FLOW.ONE">{{ t('thankyou.doNotChangePayment') }}</div>
                </template>

            </div>

            <div class="thanks__summary">
                <div class="orderSummary">
                    <div>
                        <strong>{{ t('thankyou.service') }}</strong>: <span class="capitalize">{{ customerStore.orderType }}</span>
                    </div>
                    <div v-show="scheduleStore.formattedSelectedDate && scheduleStore.formattedSelectedTime">
                        <strong>{{ t('thankyou.date') }}</strong>: <span class="capitalize"
                            v-text="scheduleStore.formattedSelectedDate"></span>, <span class="capitalize"
                            v-text="scheduleStore.formattedSelectedTime"></span>
                    </div>
                    <div v-if="customerStore.isDeliveryOrder && address">
                        <strong>{{ t('thankyou.deliveryAddress') }}</strong>: {{ address?.trim() }}
                    </div>
                    <div v-else>
                        <strong>{{ t('thankyou.pickUpAt') }} </strong>: {{ rootData.address?.full }}
                    </div>
                    <div v-if="customerStore.paymentOption && rootData?.dualpricing">
                        <strong>{{ t('thankyou.paymentMethod') }}</strong>: <span class="capitalize"
                            v-text="customerStore.paymentOption"></span>
                    </div>
                </div>
                <div class="orderSummaryHead">
                    <div>{{ t('thankyou.orderSummary') }}</div>
                </div>
                <div class="orderSummaryBody">
                    <line-items :controls="false" :hideCash="rootData?.ordering?.flow === FLOW.ONE" />
                </div>
            </div>
            <OrderTotal :hideCash="rootData?.ordering?.flow === FLOW.ONE" />
            <Payment
                v-if="isSdkReady && customerStore.paymentOption === PAYMENT.CARD && rootData?.ordering?.flow === FLOW.DEFAULT" />
        </div>
    </div>
    <ModalFooter>
        <ModalBtn class="btn-primary" @click="returnToMenu()" role="link">{{ t('thankyou.returnToMenu') }}</ModalBtn>
    </ModalFooter>

</template>

<style lang="scss" scoped>
.payNow {
    border-top: 1px solid rgba(0, 0, 0, .12);
    border-bottom: 1px solid rgba(0, 0, 0, .12);
    margin-left: -1rem;
    margin-right: -1rem;
    padding: 1rem;

    &Head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: 600;
        font-size: 16px;
        margin: 12px 0;
        line-height: 24px;
    }

    margin: 0 -1rem;
    padding: 1rem;
}

.thanks {
    height: 100%;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;

    &__payment {
        &-code {
            font-weight: 500;
            font-family: monospace;
            padding: 0.125rem 0.375rem;
            display: inline-block;
            background: #f2f2f2;
            border-radius: 4px;
            font-size: 0.875rem;
            border: 1px solid #b1b1b1;
            letter-spacing: -.25px;
            margin-left: 0.25rem;
            margin-right: 0.25rem;
        }
    }

    &__order-id {
        font-weight: 500;
        font-family: monospace;
        padding: 0.125rem 0.375rem;
        display: inline-block;
        background: #f2f2f2;
        border-radius: 4px;
        font-size: 0.875rem;
        border: 1px solid #b1b1b1;
        letter-spacing: -.25px;
        margin-left: 0.25rem;
        margin-right: 0.25rem;
    }

    &__title {
        font-size: 1.125rem;
        font-weight: 700;
        margin-bottom: 0.5rem;
        line-height: 1;
    }

    &__desc {
        font-size: 1rem;
        line-height: 1.2;
        margin-bottom: 1rem;
    }

    &__details {
        padding-bottom: 1rem;
    }

    &__note {
        font-weight: 600;
        font-size: 0.875rem;
    }
}

.capitalize {
    text-transform: capitalize;
}

.cart-total {
    margin-left: -1rem;
    margin-right: -1rem;

}

dl {
    border-top: none;
}

.orderSummary {
    border-top: 1px solid rgba(0, 0, 0, .12);
    border-bottom: 1px solid rgba(0, 0, 0, .12);
    margin-left: -1rem;
    margin-right: -1rem;
    padding: 1rem;

    &Head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: 600;
        font-size: 16px;
        margin: 12px 0;
        line-height: 24px;
    }
}

.modal {
    &__body {
        padding: 1rem;
        padding-bottom: 2rem;
        // min-height: 80vh;
        // min-height: calc(100vh - 170px);
        // min-height: calc(100svh - 170px); // 146

        @media (min-width: 576px) {
            min-height: auto;
        }
    }
}
</style>